import React, { useState, useEffect } from "react";
import MobilePopUp from "../../components/MobilePopUp/MobilePopUp";
import { useDispatch } from "react-redux";
import DataTable from "../../components/Table";
import {
  getAllFreezeData,
  freezedateAction,
  freezedateDeleteAction,
} from "../../store/actions/monthEnd";

/** API flag for new login identifier column (case-insensitive). */
const NEW_LOGIN_IDENTIFIER_YES = "yes";

/**
 * Whether API/grid value means New Login Identifier is on (matches table + create payload `True` / `Yes`).
 */
const isNewLoginIdentifierActiveFromApi = (raw) => {
  const s = String(raw ?? "")
    .trim()
    .toLowerCase();
  return (
    s === NEW_LOGIN_IDENTIFIER_YES ||
    s === "true" ||
    s === "1" ||
    s === "y"
  );
};

/** Static freeze window times (time inputs hidden; NGT-27419). */
const FREEZE_TIME_START = "00:00";
const FREEZE_TIME_END = "23:59";

/** Local calendar `YYYY-MM-DD` for `<input type="date" min>` (avoids UTC skew from `toISOString`). */
const toLocalIsoDateString = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const newLoginIdentifierColumnsFromApi = (item) => ({
  newLoginIdentifier: item.newLoginIdentifier ?? "No",
  newLoginIdentifierDateTime: isNewLoginIdentifierActiveFromApi(
    item.newLoginIdentifier,
  )
    ? item.newLoginIdentifierDateTime ?? ""
    : "",
});

/** Initial GET: prefer API `dateValue`, else legacy `freezeType`, else freeze start. */
const mapFreezeRowFromInitialFetch = (item) => ({
  id: item.id,
  createdBy: item.createdBy,
  freezeType: item.freezeType,
  freezeStartDate: item.freezeStartDate,
  freezeEndDate: item.freezeEndDate,
  dateValue: item.dateValue ?? item.freezeType ?? item.freezeStartDate,
  ...newLoginIdentifierColumnsFromApi(item),
});

/** Refetch after save/update/delete: use API `dateValue` when present (matches list payload). */
const mapFreezeRowFromListResponse = (item) => ({
  id: item.id,
  createdBy: item.createdBy,
  freezeStartDate: item.freezeStartDate,
  freezeEndDate: item.freezeEndDate,
  dateValue: item.dateValue ?? item.freezeStartDate,
  ...newLoginIdentifierColumnsFromApi(item),
});

const readUserDetailsFromStorage = () => {
  try {
    const userDetails = JSON.parse(
      localStorage.getItem("userDetails") || "null",
    );
    if (!userDetails?.user?.code) {
      return {
        ok: false,
        message: "User details not found. Please sign in again.",
      };
    }
    return { ok: true, userDetails };
  } catch {
    return {
      ok: false,
      message: "Unable to read user session. Please sign in again.",
    };
  }
};

const toTimeInputString = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/** Min time on new-login picker when date equals freeze start (01:00 vs actual start). */
const newLoginTimeInputMinOnFreezeStartDay = (freezeStartDate, freezeStartTime) => {
  const t = freezeStartTime || FREEZE_TIME_START;
  const atFreezeStart = new Date(`${freezeStartDate}T${t}`);
  const atOneAm = new Date(`${freezeStartDate}T01:00`);
  return toTimeInputString(atFreezeStart > atOneAm ? atFreezeStart : atOneAm);
};

/** Max time on new-login picker when date equals freeze end (22:59 vs actual end). */
const newLoginTimeInputMaxOnFreezeEndDay = (freezeEndDate, freezeEndTime) => {
  const t = freezeEndTime || FREEZE_TIME_END;
  const atFreezeEnd = new Date(`${freezeEndDate}T${t}`);
  const atTenFiftyNinePm = new Date(`${freezeEndDate}T22:59`);
  return toTimeInputString(atFreezeEnd < atTenFiftyNinePm ? atFreezeEnd : atTenFiftyNinePm);
};

const buildNewLoginIdentifierWindowErrorMessage = ({
  newLoginIdentifierDate,
  freezeStartDate,
  freezeEndDate,
  newLoginDT,
  minAllowed,
  maxAllowed,
}) => {
  if (newLoginIdentifierDate === freezeEndDate && newLoginDT > maxAllowed) {
    return "On the freeze end date, New Login Identifier cannot be later than 10:59. (11:59 is not allowed on the last day.)";
  }
  if (newLoginIdentifierDate === freezeStartDate && newLoginDT < minAllowed) {
    return "On the freeze start date, New Login Identifier cannot be earlier than 01:00. (00:00 is not allowed on the first day when the freeze starts at midnight.)";
  }
  return "On the freeze start date, use 01:00 or later (not 00:00); on the freeze end date, use up to 10:59 only (not 11:59), unless the actual freeze start/end times narrow this further.";
};

const FREEZE_TABLE_CELL_HEADERS = [
  "Id",
  "Created By",
  "Freeze Start Date",
  "Freeze End Date",
  "New Login Identifier",
  "New Login Identifier Date/Time",
  "Date Value",
  "",
];

const FREEZE_TABLE_KEYS = [
  "id",
  "createdBy",
  "freezeStartDate",
  "freezeEndDate",
  "newLoginIdentifier",
  "newLoginIdentifierDateTime",
  "dateValue",
  "FreezecustomButtons",
];

const formatDate = (date) => {
  if (date == null || String(date).trim() === "") {
    return "";
  }
  const parts = String(date).trim().split("-");
  if (parts.length < 3) {
    return "";
  }
  const [year, month, day] = parts;
  return `${day}-${month}-${year}`;
};

/** Normalise `<input type="time">` value to `HH:mm` for API payload (24-hour). */
const formatTime24HhMmForNewLoginPayload = (time24) => {
  if (time24 == null || time24 === "") return "";
  const parts = String(time24).trim().split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] ?? "0", 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return String(time24).trim();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const formatNewLoginIdentifierDateTimeForPayload = (yyyyMmDd, time24) =>
  `${formatDate(yyyyMmDd)} ${formatTime24HhMmForNewLoginPayload(time24)}`.trim();

const reformatDateToInputFormat = (dateString) => {
  if (dateString == null || String(dateString).trim() === "") {
    return "";
  }
  const [day, month, year] = String(dateString).trim().split("-");
  if (!day || !month || !year) {
    return "";
  }
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

const parseDateTime = (date, time) => {
  const [day, month, year] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
};

/**
 * New Login Identifier window vs freeze (01:00 on first day, 22:59 cap on last day,
 * clamped by actual freeze start/end instants from time inputs).
 */
const getNewLoginIdentifierBounds = (
  startDateStr,
  startTimeStr,
  endDateStr,
  endTimeStr,
) => {
  const startT = startTimeStr || FREEZE_TIME_START;
  const endT = endTimeStr || FREEZE_TIME_END;
  const freezeStartFull = new Date(`${startDateStr}T${startT}`);
  const freezeEndFull = new Date(`${endDateStr}T${endT}`);
  const startDayAt0100 = new Date(`${startDateStr}T01:00`);
  const endDayAt2259 = new Date(`${endDateStr}T22:59`);
  const minAllowed =
    freezeStartFull > startDayAt0100 ? freezeStartFull : startDayAt0100;
  const maxAllowed =
    freezeEndFull < endDayAt2259 ? freezeEndFull : endDayAt2259;
  return { minAllowed, maxAllowed, freezeStartFull, freezeEndFull };
};

/**
 * Parse list/API display: `DD-MM-YYYY HH:mm` (24-hour) or legacy `DD-MM-YYYY H:mm AM|PM`.
 */
const parseNewLoginIdentifierDateTimeDisplay = (value) => {
  if (value == null || typeof value !== "string") return null;
  const s = value.trim();
  const match = s.match(
    /^(\d{1,2})-(\d{1,2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?$/i,
  );
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  let hour = parseInt(match[4], 10);
  const minute = parseInt(match[5], 10);
  const mer = match[6] ? match[6].toUpperCase() : null;
  if (mer === "PM" && hour < 12) hour += 12;
  if (mer === "AM" && hour === 12) hour = 0;
  if (mer == null && (hour > 23 || minute > 59)) return null;
  const d = new Date(year, month - 1, day, hour, minute, 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Parse freeze start datetime from API/grid for "current freeze" edit lock.
 * Supports `DD-MM-YYYY HH:mm`, `YYYY-MM-DD HH:mm`, and ISO-like strings with `T`.
 */
const parseFreezeStartInstantFromApi = (freezeStartDateRaw) => {
  if (freezeStartDateRaw == null || String(freezeStartDateRaw).trim() === "") {
    return null;
  }
  const s = String(freezeStartDateRaw).trim();
  const viaDisplay = parseNewLoginIdentifierDateTimeDisplay(s);
  if (viaDisplay && !Number.isNaN(viaDisplay.getTime())) {
    return viaDisplay;
  }
  const spaceIdx = s.indexOf(" ");
  if (spaceIdx > 0) {
    const datePart = s.slice(0, spaceIdx).trim();
    const timePart = (s.slice(spaceIdx + 1).trim() || FREEZE_TIME_START).slice(
      0,
      5,
    );
    const mm = timePart.match(/^(\d{1,2}):(\d{2})/);
    const hh = mm ? String(parseInt(mm[1], 10)).padStart(2, "0") : "00";
    const mi = mm ? String(parseInt(mm[2], 10)).padStart(2, "0") : "00";
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const d = new Date(`${datePart}T${hh}:${mi}`);
      if (!Number.isNaN(d.getTime())) return d;
    }
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(datePart)) {
      const [d0, m0, y0] = datePart.split("-").map(Number);
      const d = new Date(
        y0,
        m0 - 1,
        d0,
        parseInt(hh, 10),
        parseInt(mi, 10),
        0,
        0,
      );
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  if (s.includes("T")) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const splitNewLoginDateTimeForInputs = (displayStr) => {
  const d = parseNewLoginIdentifierDateTimeDisplay(displayStr);
  if (!d) return { dateStr: "", timeStr: "" };
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { dateStr: `${y}-${mo}-${da}`, timeStr: `${hh}:${mm}` };
};

/** True when stored New Login Identifier datetime is strictly before now (edit fields must be read-only). */
const isNewLoginIdentifierEditLockedByTime = (modal) => {
  if (!modal?.id) return false;
  if (!isNewLoginIdentifierActiveFromApi(modal.newLoginIdentifier)) {
    return false;
  }
  const dt = parseNewLoginIdentifierDateTimeDisplay(
    modal.newLoginIdentifierDateTime,
  );
  if (!dt || Number.isNaN(dt.getTime())) return false;
  return Date.now() > dt.getTime();
};

const FreezeDates = () => {
  const dispatch = useDispatch();
  const [freezeStartDate, setFreezeStartDate] = useState("");
  const [freezeStartTime, setFreezeStartTime] = useState("00:00");
  const [freezeEndDate, setFreezeEndDate] = useState("");
  const [freezeEndTime, setFreezeEndTime] = useState("23:59");
  const [updateFreezeStartDateValue, setUpdateFreezeStartDateValue] = useState("");
  const [updateFreezeStartTimeValue, setUpdateFreezeStartTimeValue] = useState("00:00");
  const [updateFreezeEndDateValue, setUpdateFreezeEndDateValue] = useState("");
  const [updateFreezeEndTimeValue, setUpdateFreezeEndTimeValue] = useState("23:59");
  const [updateLoginDateValue, setUpdateLoginDateValue] = useState("");
  const [updateLoginTimeValue, setUpdateLoginTimeValue] = useState("");
  const [updateNewLoginIdentifier, setUpdateNewLoginIdentifier] = useState("No");
  const [updateNewLoginIdentifierDate, setUpdateNewLoginIdentifierDate] =
    useState("");
  const [updateNewLoginIdentifierTime, setUpdateNewLoginIdentifierTime] =
    useState("");
  const [loginDate, setLoginDate] = useState("");
  const [loginTime, setLoginTime] = useState("");
  const [selection, setSelection] = useState("Login");
  const [minDate, setMinDate] = useState("");
  const [errors, setErrors] = useState({});
  const [isEndDateTimeEnabled, setIsEndDateTimeEnabled] = useState(false);
  const [updateisEndDateTimeEnabled, setupdateIsEndDateTimeEnabled] = useState(false);
  const [showEditModal, setshowEditModal] = useState(false);
  const [showDeleteModal, setshowDeleteModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [data, setData] = useState([]);
  const [freezeStartDateTimevalidate, setFreezeStartDateTimevalidate] = useState("");
  const [curreFreezingTimeUpdate, setCurreFreezingTimeUpdate] = useState("");
  const [newLoginIdentifier, setNewLoginIdentifier] = useState("No");
  const [newLoginIdentifierDate, setNewLoginIdentifierDate] = useState("");
  const [newLoginIdentifierTime, setNewLoginIdentifierTime] = useState("");

  useEffect(() => {
    dispatch(
      getAllFreezeData((response) => {
        const rows = response?.body;
        if (!Array.isArray(rows)) {
          setData([]);
          return;
        }
        setData(rows.map(mapFreezeRowFromInitialFetch));
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    setMinDate(toLocalIsoDateString());
  }, []);

  useEffect(() => {
    if (!freezeStartDate) return;
    const el = document.getElementById("freezeEndDate");
    if (el) {
      el.setAttribute("min", freezeStartDate);
    }
  }, [freezeStartDate]);

  useEffect(() => {
    setIsEndDateTimeEnabled(Boolean(freezeStartDate));
  }, [freezeStartDate, freezeStartTime]);

  useEffect(() => {
    setupdateIsEndDateTimeEnabled(Boolean(updateFreezeStartDateValue));
  }, [updateFreezeStartDateValue]);

  useEffect(() => {
    // Auto-fill loginDate with freezeStartDate when freezeStartDate changes
    if (freezeStartDate || freezeStartTime) {
      setLoginDate(freezeStartDate);
    }
  }, [freezeStartDate]);

  useEffect(() => {
    // Auto-fill loginDate with freezeStartDate when freezeStartDate changes
    if (freezeStartTime) {
      setLoginTime(freezeStartTime);
    }
  }, [freezeStartTime]);

  useEffect(() => {
    // Auto-fill loginDate with freezeStartDate when freezeStartDate changes
    if (updateFreezeStartDateValue) {
      setUpdateLoginDateValue(updateFreezeStartDateValue);
    }
  }, [updateFreezeStartDateValue]);

  useEffect(() => {
    // Auto-fill loginDate with freezeStartDate when freezeStartDate changes
    if (updateFreezeStartTimeValue) {
      setUpdateLoginTimeValue(updateFreezeStartTimeValue);
    }
  }, [updateFreezeStartTimeValue]);

  const resetCreateFreezeForm = () => {
    setFreezeStartDate("");
    setFreezeStartTime(FREEZE_TIME_START);
    setFreezeEndDate("");
    setFreezeEndTime(FREEZE_TIME_END);
    setLoginDate("");
    setLoginTime("");
    setNewLoginIdentifier("No");
    setNewLoginIdentifierDate("");
    setNewLoginIdentifierTime("");
  };

  const resetUpdateFreezeForm = () => {
    setUpdateFreezeStartDateValue("");
    setUpdateFreezeStartTimeValue(FREEZE_TIME_START);
    setUpdateFreezeEndDateValue("");
    setUpdateFreezeEndTimeValue(FREEZE_TIME_END);
    setUpdateLoginDateValue("");
    setUpdateLoginTimeValue("");
    setUpdateNewLoginIdentifier("No");
    setUpdateNewLoginIdentifierDate("");
    setUpdateNewLoginIdentifierTime("");
  };

  const applyFreezeListResponseToTable = (response) => {
    const rows = response?.body;
    if (!Array.isArray(rows)) {
      setData([]);
      return;
    }
    setData(rows.map(mapFreezeRowFromListResponse));
  };

  const handleSave = async () => {
    const auth = readUserDetailsFromStorage();
    if (!auth.ok) {
      setErrors({ global: auth.message });
      return;
    }
    const { userDetails } = auth;

    let validationErrors = {};

    // Check if required fields are empty
    if (!freezeStartDate)
      validationErrors.freezeStartDate = "Freeze Start Date is required";
    // if (!freezeStartTime)
    //   validationErrors.freezeStartTime = "Freeze Start Time is required";
    if (!freezeEndDate)
      validationErrors.freezeEndDate = "Freeze End Date is required";
    // if (!freezeEndTime)
    //   validationErrors.freezeEndTime = "Freeze End Time is required";
    if (!loginDate) validationErrors.loginDate = "Date is required";
    // if (!loginTime) validationErrors.loginTime = "Time is required";
    if (newLoginIdentifier === "Yes") {
      if (!newLoginIdentifierDate)
        validationErrors.newLoginIdentifierDate =
          "New Login Identifier date is required";
      if (!newLoginIdentifierTime)
        validationErrors.newLoginIdentifierTime =
          "New Login Identifier time is required";
    }

    // Set errors and return if there are any
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    const currentDateTime = new Date();
    const freezeStartDateTime = new Date(`${freezeStartDate}T${FREEZE_TIME_START}`);
    const freezeEndDateTime = new Date(`${freezeEndDate}T${FREEZE_TIME_END}`);

    // Check if freezeStartDateTime and freezeEndDateTime are not greater than currentDateTime
    if (
      freezeStartDateTime < currentDateTime ||
      freezeEndDateTime < currentDateTime
    ) {
      setErrors({
        global:
          "Freeze Start DateTime and Freeze End DateTime cannot be in the past.",
      });
      return;
    }

    const diffInTime = freezeEndDateTime - freezeStartDateTime;
    const diffInDays = diffInTime / (1000 * 3600 * 24);

    if (diffInDays > 15) {
      setErrors({
        global:
          "The difference between Freeze Start and End dates cannot be greater than 15 days.",
      });
      setLoginDate(freezeStartDate);
      setLoginTime(freezeStartTime || FREEZE_TIME_START);
      setFreezeEndDate("");
      setFreezeEndTime(FREEZE_TIME_END);
      return;
    }

    const loginTimeForParse = loginTime || FREEZE_TIME_START;
    const formattedFreezeStartDateTime = `${formatDate(freezeStartDate)} ${FREEZE_TIME_START}`;
    const formattedFreezeEndDateTime = `${formatDate(freezeEndDate)} ${FREEZE_TIME_END}`;
    const parFreezeStartDateTime = parseDateTime(
      formatDate(freezeStartDate),
      FREEZE_TIME_START,
    );
    const parformattedFreezeEndDateTime = parseDateTime(
      formatDate(freezeEndDate),
      FREEZE_TIME_END,
    );
    const parformattedLoginDate = parseDateTime(
      formatDate(loginDate),
      loginTimeForParse,
    );

    if (
      Number.isNaN(parformattedLoginDate.getTime()) ||
      Number.isNaN(parFreezeStartDateTime.getTime()) ||
      Number.isNaN(parformattedFreezeEndDateTime.getTime())
    ) {
      setErrors({
        global: "Invalid login date or time. Please check your entries.",
      });
      return;
    }

    if (
      parformattedLoginDate < parFreezeStartDateTime ||
      parformattedLoginDate > parformattedFreezeEndDateTime
    ) {
      setErrors({
        global:
          "DateTime must be between Freeze Start DateTime and Freeze End DateTime.",
      });
      return;
    }

    if (newLoginIdentifier === "Yes") {
      const newLoginDT = new Date(
        `${newLoginIdentifierDate}T${newLoginIdentifierTime}`,
      );

      if (Number.isNaN(newLoginDT.getTime())) {
        setErrors({
          global: "Invalid New Login Identifier date or time.",
        });
        return;
      }

      if (
        newLoginIdentifierDate < freezeStartDate ||
        newLoginIdentifierDate > freezeEndDate
      ) {
        setErrors({
          newLoginIdentifierDate:
            "Date must be between Freeze Start Date and Freeze End Date.",
        });
        return;
      }

      const {
        minAllowed,
        maxAllowed,
        freezeStartFull,
        freezeEndFull,
      } = getNewLoginIdentifierBounds(
        freezeStartDate,
        freezeStartTime,
        freezeEndDate,
        freezeEndTime,
      );

      if (newLoginDT.getTime() <= freezeStartFull.getTime()) {
        setErrors({
          global:
            "On the freeze start date, New Login Identifier cannot be earlier than 01:00. (00:00 is not allowed on the first day when the freeze starts at midnight.)",
        });
        return;
      }

      if (newLoginDT.getTime() >= freezeEndFull.getTime()) {
        setErrors({
          global:
            "On the freeze end date, New Login Identifier cannot be later than 10:59. (11:59 is not allowed on the last day.)",
        });
        return;
      }

      if (minAllowed.getTime() > maxAllowed.getTime()) {
        setErrors({
          global: "Freeze range is invalid for New Login Identifier.",
        });
        return;
      }

      if (newLoginDT < minAllowed || newLoginDT > maxAllowed) {
        setErrors({
          global: buildNewLoginIdentifierWindowErrorMessage({
            newLoginIdentifierDate,
            freezeStartDate,
            freezeEndDate,
            newLoginDT,
            minAllowed,
            maxAllowed,
          }),
        });
        return;
      }
    }

    const formData = {
      createdBy: userDetails.user.code,
      //freezeType: selection.toUpperCase(),
      freezeStartDate: formattedFreezeStartDateTime,
      freezeEndDate: formattedFreezeEndDateTime,
      dateValue: formattedFreezeStartDateTime,
      newLoginIdentifier: newLoginIdentifier === "Yes" ? "Yes" : "No",
      ...(newLoginIdentifier === "Yes"
        ? {
            newLoginIdentifierDateTime:
              formatNewLoginIdentifierDateTimeForPayload(
                newLoginIdentifierDate,
                newLoginIdentifierTime,
              ),
          }
        : {}),
    };
    try {
      // Dispatch the freezeDateAction and wait for it to complete
      await dispatch(freezedateAction(formData, (res) => {
        if (res?.data?.body) {
          resetCreateFreezeForm();
          dispatch(getAllFreezeData(applyFreezeListResponseToTable));
        }
      }))

    } catch (error) {
      console.error("Error during saving:", error);
      // Handle any errors that might occur during the dispatch
    }
  };

  useEffect(() => {
    // `{}` is truthy; only populate edit fields when a real grid row is selected (has id).
    if (!modalData || !modalData.id) {
      return;
    }

    // Reformat and set freezeStartDate
    if (modalData.freezeStartDate) {
      const rawStart = String(modalData.freezeStartDate);
      const [startDatePart] = rawStart.split(" ");
      if (startDatePart) {
        setUpdateFreezeStartDateValue(reformatDateToInputFormat(startDatePart));
      }

      const checkCurrentDate = new Date();
      const freezeStartInstant = parseFreezeStartInstantFromApi(
        modalData.freezeStartDate,
      );
      if (freezeStartInstant && !Number.isNaN(freezeStartInstant.getTime())) {
        setFreezeStartDateTimevalidate(freezeStartInstant);
        setCurreFreezingTimeUpdate(checkCurrentDate > freezeStartInstant);
      } else {
        setFreezeStartDateTimevalidate("");
        setCurreFreezingTimeUpdate(false);
      }
    }

    // Reformat and set freezeEndDate
    if (modalData.freezeEndDate) {
      const rawEnd = String(modalData.freezeEndDate);
      const [endDatePart] = rawEnd.split(" ");
      if (endDatePart) {
        setUpdateFreezeEndDateValue(reformatDateToInputFormat(endDatePart));
      }
    }

    // Reformat and set dateValue
    if (modalData.dateValue) {
      const dv = String(modalData.dateValue).trim();
      if (dv) {
        setUpdateLoginDateValue(reformatDateToInputFormat(dv));
      }
    }

    const nlActive = isNewLoginIdentifierActiveFromApi(
      modalData.newLoginIdentifier,
    );
    setUpdateNewLoginIdentifier(nlActive ? "Yes" : "No");
    if (nlActive && modalData.newLoginIdentifierDateTime) {
      const { dateStr, timeStr } = splitNewLoginDateTimeForInputs(
        modalData.newLoginIdentifierDateTime,
      );
      setUpdateNewLoginIdentifierDate(dateStr);
      setUpdateNewLoginIdentifierTime(timeStr || "00:00");
    } else {
      setUpdateNewLoginIdentifierDate("");
      setUpdateNewLoginIdentifierTime("");
    }
  }, [modalData]);

  const updatefreezeData = async () => {
    if (!modalData?.id) {
      setErrors({
        global: "No freeze record selected. Please try again.",
      });
      return;
    }

    let validationErrors = {};

    // Check if required fields are empty
    if (!updateFreezeStartDateValue)
      validationErrors.updateFreezeStartDateValue =
        "Freeze Start Date is required";
    // if (!updateFreezeStartTimeValue)
    //   validationErrors.updateFreezeStartTimeValue =
    //     "Freeze Start Time is required";
    if (!updateFreezeEndDateValue)
      validationErrors.updateFreezeEndDateValue = "Freeze End Date is required";
    // if (!updateFreezeEndTimeValue)
    //   validationErrors.updateFreezeEndTimeValue = "Freeze End Time is required";
    if (!updateLoginDateValue)
      validationErrors.updateLoginDateValue = "Date is required";
    // if (!updateLoginTimeValue)
    //   validationErrors.updateLoginTimeValue = "Time is required";

    // Set errors and return if there are any
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Clear errors if validation passes
    setErrors({});

    const updatecurrentDateTime = new Date();
    const updatefreezeStartDateTime = new Date(
      `${updateFreezeStartDateValue}T${FREEZE_TIME_START}`,
    );
    const updatefreezeEndDateTime = new Date(
      `${updateFreezeEndDateValue}T${FREEZE_TIME_END}`,
    );
    if (curreFreezingTimeUpdate) {
      if (updatefreezeEndDateTime < updatecurrentDateTime) {
        setErrors({
          global: "Freeze End DateTime cannot be in the past.",
        });
        return;
      }
    } else if (
      updatefreezeStartDateTime < updatecurrentDateTime ||
      updatefreezeEndDateTime < updatecurrentDateTime
    ) {
      setErrors({
        global:
          "Freeze Start DateTime and Freeze End DateTime cannot be in the past.",
      });
      return;
    }

    const updatediffInTime =
      updatefreezeEndDateTime - updatefreezeStartDateTime;
    const updatediffInDays = updatediffInTime / (1000 * 3600 * 24);

    if (updatediffInDays > 15) {
      setErrors({
        global:
          "The difference between Freeze Start and End dates cannot be greater than 15 days.",
      });
      setUpdateLoginDateValue(updateFreezeStartDateValue);
      setUpdateLoginTimeValue(updateFreezeStartTimeValue || FREEZE_TIME_START);
      setUpdateFreezeEndDateValue("");
      setUpdateFreezeEndTimeValue(FREEZE_TIME_END);
      return;
    }

    const updateformattedFreezeStartDateTime = `${formatDate(updateFreezeStartDateValue)} ${FREEZE_TIME_START}`;
    const updateformattedFreezeEndDateTime = `${formatDate(updateFreezeEndDateValue)} ${FREEZE_TIME_END}`;

    const updateformattedFreezeStartDateTimeVal = parseDateTime(
      formatDate(updateFreezeStartDateValue),
      FREEZE_TIME_START,
    );
    const updateformattedFreezeEndDateTimeVal = parseDateTime(
      formatDate(updateFreezeEndDateValue),
      FREEZE_TIME_END,
    );
    const updateformattedLoginDateVal = parseDateTime(
      formatDate(updateLoginDateValue),
      updateLoginTimeValue || FREEZE_TIME_START,
    );

    if (
      Number.isNaN(updateformattedLoginDateVal.getTime()) ||
      Number.isNaN(updateformattedFreezeStartDateTimeVal.getTime()) ||
      Number.isNaN(updateformattedFreezeEndDateTimeVal.getTime())
    ) {
      setErrors({
        global: "Invalid login date or time. Please check your entries.",
      });
      return;
    }

    if (
      updateformattedLoginDateVal < updateformattedFreezeStartDateTimeVal ||
      updateformattedLoginDateVal > updateformattedFreezeEndDateTimeVal
    ) {
      setErrors({
        global:
          "Login DateTime must be between Freeze Start DateTime and Freeze End DateTime.",
      });
      return;
    }

    const editNlTimeLocked = isNewLoginIdentifierEditLockedByTime(modalData);

    if (updateNewLoginIdentifier === "Yes") {
      if (!editNlTimeLocked) {
        if (!updateNewLoginIdentifierDate) {
          setErrors({
            updateNewLoginIdentifierDate:
              "New Login Identifier date is required",
          });
          return;
        }
        if (!updateNewLoginIdentifierTime) {
          setErrors({
            updateNewLoginIdentifierTime:
              "New Login Identifier time is required",
          });
          return;
        }
      } else if (
        !updateNewLoginIdentifierDate ||
        !updateNewLoginIdentifierTime
      ) {
        setErrors({
          global:
            "New Login Identifier date and time are missing for this record.",
        });
        return;
      }

      const newLoginDT = new Date(
        `${updateNewLoginIdentifierDate}T${updateNewLoginIdentifierTime}`,
      );

      if (Number.isNaN(newLoginDT.getTime())) {
        setErrors({
          global: "Invalid New Login Identifier date or time.",
        });
        return;
      }

      if (
        updateNewLoginIdentifierDate < updateFreezeStartDateValue ||
        updateNewLoginIdentifierDate > updateFreezeEndDateValue
      ) {
        setErrors({
          updateNewLoginIdentifierDate:
            "Date must be between Freeze Start Date and Freeze End Date.",
        });
        return;
      }

      const effUpdateStartTime = updateFreezeStartTimeValue || FREEZE_TIME_START;
      const effUpdateEndTime = updateFreezeEndTimeValue || FREEZE_TIME_END;

      const {
        minAllowed: updateNlMinAllowed,
        maxAllowed: updateNlMaxAllowed,
        freezeStartFull: updateFreezeStartFull,
        freezeEndFull: updateFreezeEndFull,
      } = getNewLoginIdentifierBounds(
        updateFreezeStartDateValue,
        effUpdateStartTime,
        updateFreezeEndDateValue,
        effUpdateEndTime,
      );

      if (newLoginDT.getTime() <= updateFreezeStartFull.getTime()) {
        setErrors({
          global:
            "On the freeze start date, New Login Identifier cannot be earlier than 01:00. (00:00 is not allowed on the first day when the freeze starts at midnight.)",
        });
        return;
      }

      if (newLoginDT.getTime() >= updateFreezeEndFull.getTime()) {
        setErrors({
          global:
            "On the freeze end date, New Login Identifier cannot be later than 10:59. (11:59 is not allowed on the last day.)",
        });
        return;
      }

      if (updateNlMinAllowed.getTime() > updateNlMaxAllowed.getTime()) {
        setErrors({
          global: "Freeze range is invalid for New Login Identifier.",
        });
        return;
      }

      if (newLoginDT < updateNlMinAllowed || newLoginDT > updateNlMaxAllowed) {
        setErrors({
          global: buildNewLoginIdentifierWindowErrorMessage({
            newLoginIdentifierDate: updateNewLoginIdentifierDate,
            freezeStartDate: updateFreezeStartDateValue,
            freezeEndDate: updateFreezeEndDateValue,
            newLoginDT,
            minAllowed: updateNlMinAllowed,
            maxAllowed: updateNlMaxAllowed,
          }),
        });
        return;
      }
    }

    const formData = {
      id: modalData.id,
      freezeStartDate: updateformattedFreezeStartDateTime,
      freezeEndDate: updateformattedFreezeEndDateTime,
      dateValue: updateformattedFreezeStartDateTime,
      newLoginIdentifier: updateNewLoginIdentifier === "Yes" ? "Yes" : "No",
      ...(updateNewLoginIdentifier === "Yes"
        ? {
            newLoginIdentifierDateTime:
              formatNewLoginIdentifierDateTimeForPayload(
                updateNewLoginIdentifierDate,
                updateNewLoginIdentifierTime,
              ),
          }
        : {}),
    };
    try {
      // Dispatch the freezeDateAction and wait for it to complete
      await dispatch(freezedateAction(formData, (res) => {
        if (res?.data?.body) {
          setshowEditModal(false);
          resetUpdateFreezeForm();
          dispatch(getAllFreezeData(applyFreezeListResponseToTable));
        }
      }))
     
     
    } catch (error) {
      console.error("Error during saving:", error);
      // Handle any errors that might occur during the dispatch
    }
  };

  const deleteData = async (row) => {
    if (!row?.id) {
      return;
    }
    try {
      await dispatch(
        freezedateDeleteAction(row, (res) => {
          if (res?.data?.body === true) {
            setshowDeleteModal(false);
            dispatch(getAllFreezeData(applyFreezeListResponseToTable));
          }
        }),
      );
    } catch (error) {
      console.error("Error during delete:", error);
    }
  };

  const editNewLoginLockedByPast =
    showEditModal && isNewLoginIdentifierEditLockedByTime(modalData);

  return (
    <>
      <div className="month-end-config-page__panel">
        <form className="month-end-config-page__form createForm">
          <div className="row dNone">
            <div className="mb-3 col-6">
              <label htmlFor="selection" className="month-end-config-page__label">
                Select Option
              </label>
              <div className="custom-select-container">
                <select
                  id="selection"
                  className="month-end-config-page__select"
                  value={selection}
                  onChange={(e) => setSelection(e.target.value)}
                >
                  <option value="Login">Login</option>
                  <option value="Issuance">Issuance</option>
                </select>
              </div>
            </div>
          </div>

          <div className="month-end-config-page__compact-grid">
            <div className="month-end-config-page__field">
              <label htmlFor="freezeStartDate" className="month-end-config-page__label">
                Freeze Start Date
              </label>
              <input
                type="date"
                id="freezeStartDate"
                className="month-end-config-page__input month-end-config-page__input--date"
                value={freezeStartDate}
                onChange={(e) => {
                  setFreezeStartDate(e.target.value);
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    freezeStartDate: "",
                  }));
                }}
                min={minDate}
                max={freezeEndDate}
              />
              {errors.freezeStartDate && (
                <div className="text-danger">{errors.freezeStartDate}</div>
              )}
            </div>

            <div className="month-end-config-page__field">
              <label htmlFor="freezeEndDate" className="month-end-config-page__label">
                Freeze End Date
              </label>
              <input
                type="date"
                id="freezeEndDate"
                className="month-end-config-page__input month-end-config-page__input--date"
                value={freezeEndDate}
                onChange={(e) => {
                  setFreezeEndDate(e.target.value);
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    freezeEndDate: "",
                  }));
                }}
                min={freezeStartDate || minDate}
                disabled={!isEndDateTimeEnabled}
              />
              {errors.freezeEndDate && (
                <div className="text-danger">{errors.freezeEndDate}</div>
              )}
            </div>

            <div className="month-end-config-page__field">
              <label htmlFor="newLoginIdentifier" className="month-end-config-page__label">
                New Login Identifier
              </label>
              <div className="custom-select-container">
                <select
                  id="newLoginIdentifier"
                  className="month-end-config-page__select"
                  value={newLoginIdentifier}
                  onChange={(e) => {
                    const v = e.target.value;
                    setNewLoginIdentifier(v);
                    if (v === "No") {
                      setNewLoginIdentifierDate("");
                      setNewLoginIdentifierTime("");
                    }
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      newLoginIdentifierDate: "",
                      newLoginIdentifierTime: "",
                    }));
                  }}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </select>
              </div>
            </div>
          </div>

          {newLoginIdentifier === "Yes" && (
            <div className="month-end-config-page__compact-grid month-end-config-page__compact-grid--login-id">
              <div className="month-end-config-page__field">
                <label
                  htmlFor="newLoginIdentifierDate"
                  className="month-end-config-page__label"
                >
                  New Login Identifier Date
                </label>
                <input
                  type="date"
                  id="newLoginIdentifierDate"
                  className="month-end-config-page__input month-end-config-page__input--date"
                  value={newLoginIdentifierDate}
                  onChange={(e) => {
                    setNewLoginIdentifierDate(e.target.value);
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      newLoginIdentifierDate: "",
                    }));
                  }}
                  min={
                    freezeStartDate
                      ? freezeStartDate >= minDate
                        ? freezeStartDate
                        : minDate
                      : minDate
                  }
                  max={freezeEndDate || undefined}
                />
                {errors.newLoginIdentifierDate && (
                  <div className="text-danger">
                    {errors.newLoginIdentifierDate}
                  </div>
                )}
              </div>

              <div className="month-end-config-page__field" lang="en-GB">
                <label
                  htmlFor="newLoginIdentifierTime"
                  className="month-end-config-page__label"
                >
                  New Login Identifier Time
                </label>
                <input
                  type="time"
                  step={60}
                  id="newLoginIdentifierTime"
                  className="month-end-config-page__input month-end-config-page__input--date"
                  value={newLoginIdentifierTime}
                  min={
                    newLoginIdentifierDate &&
                    freezeStartDate &&
                    newLoginIdentifierDate === freezeStartDate
                      ? newLoginTimeInputMinOnFreezeStartDay(
                          freezeStartDate,
                          freezeStartTime,
                        )
                      : undefined
                  }
                  max={
                    newLoginIdentifierDate &&
                    freezeEndDate &&
                    newLoginIdentifierDate === freezeEndDate
                      ? newLoginTimeInputMaxOnFreezeEndDay(
                          freezeEndDate,
                          freezeEndTime,
                        )
                      : undefined
                  }
                  onChange={(e) => {
                    setNewLoginIdentifierTime(e.target.value);
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      newLoginIdentifierTime: "",
                    }));
                  }}
                />
                {errors.newLoginIdentifierTime && (
                  <div className="text-danger">
                    {errors.newLoginIdentifierTime}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="row dNone">
            <div className="mb-3 col-8 col-lg-4">
              <label htmlFor="loginDate" className="month-end-config-page__label">
                {selection === "Login"
                  ? "Login Start Date"
                  : "Issuance Start Date"}
              </label>
              <input
                type="date"
                id="loginDate"
                className="month-end-config-page__input month-end-config-page__input--date"
                value={loginDate}
                onChange={(e) => {
                  setLoginDate(e.target.value);
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    loginDate: "",
                  }));
                }}
                min={freezeStartDate || minDate}
                max={freezeEndDate}
              />
              {errors.loginDate && (
                <div className="text-danger">{errors.loginDate}</div>
              )}
            </div>

            <div className="mb-3 col-4 col-lg-2">
              <input
                type="time"
                id="loginTime"
                className="month-end-config-page__input month-end-config-page__input--date"
                value={loginTime}
                onChange={(e) => {
                  setLoginTime(e.target.value);
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    loginTime: "",
                  }));
                }}
              />
              {errors.loginTime && (
                <div className="text-danger">{errors.loginTime}</div>
              )}
            </div>
          </div>

          {errors.global && (
            <div className="month-end-config-page__error" role="alert">
              {errors.global}
            </div>
          )}

          <div className="month-end-config-page__actions">
            <button
              type="button"
              className="month-end-config-page__btn month-end-config-page__btn--primary"
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <div className="month-end-config-page__note">
        <div className="month-end-config-page__note-title">Rules</div>
        <div className="month-end-config-page__note-points">
          <p>1. Only future freezes can be created.</p>
          <p>2. Overlapping freeze dates are not allowed.</p>
          <p>3. Current freeze dates cannot be edited or deleted.</p>
        </div>
      </div>

      {data?.length > 0 && (
        <div className="proposal-dashboard__table-section month-end-config-page__table-section">
            <DataTable
              cellHeaders={FREEZE_TABLE_CELL_HEADERS}
              data={data}
              keys={FREEZE_TABLE_KEYS}
              fileName="Freeze Data"
              addSerialNo={false}
              onCustomButtonClick={(item) => {
                setModalData(item);
                setUpdateNewLoginIdentifier(
                  isNewLoginIdentifierActiveFromApi(item.newLoginIdentifier)
                    ? "Yes"
                    : "No",
                );
                setshowEditModal(true);
              }}
              onCustomDeletButtonClick={(item) => {
                setModalData(item);
                setshowDeleteModal(true);
              }}
              convertValueToString={true}
              showExcelDownload={true}
              PerPageApiData={false}
              paginationItems={data}
              alwaysShown={false}
            />
        </div>
      )}
      {showEditModal && modalData?.id != null && (
        <MobilePopUp onClose={() => setshowEditModal(false)}>
          <div className="update-form month-end-config-page__modal-form freeze-edit-modal">
            <h3 className="month-end-config-page__modal-title">Edit freeze</h3>
            <p className="month-end-config-page__modal-subtitle">
              Adjust freeze dates. New Login Identifier reflects the saved row;
              it locks when the saved date and time are no longer in the future.
            </p>

            <div className="month-end-config-page__modal-section month-end-config-page__modal-section--muted">
              <div className="month-end-config-page__modal-section-title">Freeze period</div>
              <div className="month-end-config-page__compact-grid month-end-config-page__compact-grid--modal">
                <div className="month-end-config-page__field">
                  <label
                    htmlFor="updateFreezeStartDate"
                    className="month-end-config-page__label"
                  >
                    Freeze Start Date
                  </label>
                  <input
                    type="date"
                    id="updateFreezeStartDate"
                    className="month-end-config-page__input month-end-config-page__input--date"
                    value={updateFreezeStartDateValue}
                    onChange={(e) => {
                      setUpdateFreezeStartDateValue(e.target.value);
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        updateFreezeStartDateValue: "",
                      }));
                    }}
                    min={minDate}
                    max={updateFreezeEndDateValue}
                    disabled={curreFreezingTimeUpdate}
                  />
                  {errors.updateFreezeStartDateValue && (
                    <div className="text-danger">
                      {errors.updateFreezeStartDateValue}
                    </div>
                  )}
                </div>

                <div className="month-end-config-page__field">
                  <label
                    htmlFor="updateFreezeEndDate"
                    className="month-end-config-page__label"
                  >
                    Freeze End Date
                  </label>
                  <input
                    type="date"
                    id="updateFreezeEndDate"
                    className="month-end-config-page__input month-end-config-page__input--date"
                    value={updateFreezeEndDateValue}
                    onChange={(e) => {
                      setUpdateFreezeEndDateValue(e.target.value);
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        updateFreezeEndDateValue: "",
                      }));
                    }}
                    min={updateFreezeStartDateValue}
                    disabled={!updateisEndDateTimeEnabled}
                  />
                  {errors.updateFreezeEndDateValue && (
                    <div className="text-danger">
                      {errors.updateFreezeEndDateValue}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="month-end-config-page__modal-section">
              {editNewLoginLockedByPast && (
                <p
                  className="month-end-config-page__modal-hint"
                  role="status"
                >
                  The saved New Login Identifier date and time are before the
                  current moment. The dropdown and date/time fields cannot be
                  changed.
                </p>
              )}
              <div className="month-end-config-page__compact-grid month-end-config-page__compact-grid--modal">
                <div className="month-end-config-page__field">
                  <label
                    htmlFor="updateNewLoginIdentifier"
                    className="month-end-config-page__label"
                  >
                    New Login Identifier
                  </label>
                  <div className="custom-select-container">
                    <select
                      id="updateNewLoginIdentifier"
                      className="month-end-config-page__select"
                      value={updateNewLoginIdentifier}
                      disabled={editNewLoginLockedByPast}
                      onChange={(e) => {
                        const v = e.target.value;
                        setUpdateNewLoginIdentifier(v);
                        if (v === "No") {
                          setUpdateNewLoginIdentifierDate("");
                          setUpdateNewLoginIdentifierTime("");
                        }
                        setErrors((prev) => ({
                          ...prev,
                          updateNewLoginIdentifierDate: "",
                          updateNewLoginIdentifierTime: "",
                        }));
                      }}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                </div>

                {updateNewLoginIdentifier === "Yes" && (
                  <>
                    <div className="month-end-config-page__field">
                      <label
                        htmlFor="updateNewLoginIdentifierDate"
                        className="month-end-config-page__label"
                      >
                        New Login Identifier Date
                      </label>
                      <input
                        type="date"
                        id="updateNewLoginIdentifierDate"
                        className="month-end-config-page__input month-end-config-page__input--date"
                        value={updateNewLoginIdentifierDate}
                        disabled={editNewLoginLockedByPast}
                        min={updateFreezeStartDateValue || minDate}
                        max={updateFreezeEndDateValue || undefined}
                        onChange={(e) => {
                          setUpdateNewLoginIdentifierDate(e.target.value);
                          setErrors((prev) => ({
                            ...prev,
                            updateNewLoginIdentifierDate: "",
                          }));
                        }}
                      />
                      {errors.updateNewLoginIdentifierDate && (
                        <div className="text-danger">
                          {errors.updateNewLoginIdentifierDate}
                        </div>
                      )}
                    </div>
                    <div className="month-end-config-page__field" lang="en-GB">
                      <label
                        htmlFor="updateNewLoginIdentifierTime"
                        className="month-end-config-page__label"
                      >
                        New Login Identifier Time
                      </label>
                      <input
                        type="time"
                        step={60}
                        id="updateNewLoginIdentifierTime"
                        className="month-end-config-page__input month-end-config-page__input--date"
                        value={updateNewLoginIdentifierTime}
                        disabled={editNewLoginLockedByPast}
                        onChange={(e) => {
                          setUpdateNewLoginIdentifierTime(e.target.value);
                          setErrors((prev) => ({
                            ...prev,
                            updateNewLoginIdentifierTime: "",
                          }));
                        }}
                      />
                      {errors.updateNewLoginIdentifierTime && (
                        <div className="text-danger">
                          {errors.updateNewLoginIdentifierTime}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="row dNone">
              <div className="mb-3 col-8">
                <label htmlFor="loginDate" className="month-end-config-page__label">
                  {selection === "Login"
                    ? "Login Start Date"
                    : "Issuance Start Date"}
                </label>
                <input
                  type="date"
                  id="updateLoginDate"
                  className="month-end-config-page__input month-end-config-page__input--date"
                  value={updateLoginDateValue}
                  onChange={(e) => {
                    setUpdateLoginDateValue(e.target.value);
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      updateLoginDateValue: "",
                    }));
                  }}
                  min={updateFreezeStartDateValue || minDate}
                  max={updateFreezeEndDateValue}
                />
                {errors.updateLoginDateValue && (
                  <div className="text-danger">
                    {errors.updateLoginDateValue}
                  </div>
                )}
              </div>
              <div className="mb-3 col-4">
                <input
                  type="time"
                  id="updateLoginTime"
                  className="month-end-config-page__input month-end-config-page__input--date"
                  value={updateLoginTimeValue}
                  onChange={(e) => {
                    setUpdateLoginTimeValue(e.target.value);
                    setErrors((prevErrors) => ({
                      ...prevErrors,
                      updateLoginTimeValue: "",
                    }));
                  }}
                />
                {errors.updateLoginTimeValue && (
                  <div className="text-danger">
                    {errors.updateLoginTimeValue}
                  </div>
                )}
              </div>
            </div>

            {errors.global && (
              <div className="month-end-config-page__error" role="alert">
                {errors.global}
              </div>
            )}

            <div className="month-end-config-page__modal-actions">
              <button
                type="button"
                className="month-end-config-page__btn month-end-config-page__btn--secondary"
                onClick={() => setshowEditModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="month-end-config-page__btn month-end-config-page__btn--primary"
                onClick={() => updatefreezeData()}
              >
                Update
              </button>
            </div>
          </div>
        </MobilePopUp>
      )}

      {showDeleteModal && modalData?.id != null && (
        <MobilePopUp
          onClose={() => {
            setshowDeleteModal(false);
          }}
        >
          <div className="month-end-config-page__delete-modal">
            <div className="month-end-config-page__delete-title">
              Are you sure?
            </div>
            <div className="month-end-config-page__delete-text">
              You won&apos;t be able to revert this!
            </div>
            <div className="month-end-config-page__delete-actions">
              <button
                type="button"
                className="month-end-config-page__btn month-end-config-page__btn--secondary"
                onClick={() => setshowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="month-end-config-page__btn month-end-config-page__btn--danger"
                onClick={() => deleteData(modalData)}
              >
                Yes, delete it!
              </button>
            </div>
          </div>
        </MobilePopUp>
      )}
    </>
  );
};

export default FreezeDates;